import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Icon as OldIcon } from '@/components'
import CellIcon from '@/components/cell/atoms/Icon'
import { IconPack } from '@/components/cell/atoms/Icon/enum'
import theme from '@/theme'
import { generateTagId } from '@/utils/test-automation'
import COLOR from '@/components/cell/designTokens/color'
import { useLocales } from '@/shared/hooks/useLocales'

type OldIconName = 'menu'

export interface Props {
  pageBanner?: React.ReactNode
  label?: string
  logo: { url: string; alt: string }
  leftIcon?: IconPack | OldIconName
  ariaLabelLeftIcon?: string
  rightIcon?: IconPack | OldIconName
  disabledSearch?: boolean
  disabledSignIn?: boolean
  profileImageURL?: string
  onClickLogo?: () => void
  onClickLeftIcon?: () => void
  onClickRightIcon?: () => void
  onClickSearchIcon?: () => void
  onClickSignInButton?: (isSignin: boolean) => void
}

const GlobalHeader = ({
  label,
  logo,
  leftIcon,
  ariaLabelLeftIcon,
  rightIcon,
  profileImageURL,
  disabledSignIn = false,
  disabledSearch = false,
  onClickLogo,
  onClickLeftIcon,
  onClickRightIcon,
  onClickSearchIcon,
  onClickSignInButton,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const { isLocale } = useLocales()
  const isSignInButtonVisible = !disabledSignIn

  return (
    <StyledContainer aria-label={label}>
      <StyledHeader>
        <StyledLeftButtonGroup>
          {leftIcon && (
            <StyledLeftIconWrapper>
              <StyledLeftIconButton aria-label={ariaLabelLeftIcon} onClick={onClickLeftIcon}>
                <HeaderIcon name={leftIcon} />
              </StyledLeftIconButton>
            </StyledLeftIconWrapper>
          )}
          <StyledLogoWrapper
            role="button"
            tabIndex={0}
            onClick={onClickLogo}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onClickLogo?.()
              }
            }}
          >
            <img src={logo.url} alt={logo.alt} />
          </StyledLogoWrapper>
        </StyledLeftButtonGroup>
        <StyledRightButtonGroup $hasSearchIcon={!disabledSearch}>
          {!disabledSearch && (
            <StyledButton aria-label="Search" onClick={onClickSearchIcon}>
              <HeaderIcon name={IconPack.HEADER_SEARCH_BLACK} />
            </StyledButton>
          )}
          {rightIcon && (
            <StyledButton onClick={onClickRightIcon}>
              <HeaderIcon name={rightIcon} />
            </StyledButton>
          )}
          {isSignInButtonVisible &&
            (profileImageURL ? (
              <StyledButton
                id={generateTagId('all', 'header', 'btn', 'profile')}
                onClick={() => onClickSignInButton?.(true)}
              >
                <StyledUserImage src={profileImageURL} alt="user profile" />
              </StyledButton>
            ) : (
              <StyledSignInButton
                id={generateTagId('all', 'header', 'btn', 'login')}
                onClick={() => onClickSignInButton?.(false)}
              >
                {t('common.signIn')}/{t('common.registration')}
              </StyledSignInButton>
            ))}
        </StyledRightButtonGroup>
      </StyledHeader>
    </StyledContainer>
  )
}

const HeaderIcon = ({ name }: { name: IconPack | OldIconName }): JSX.Element => {
  return Object.values(IconPack).includes(name as IconPack) ? (
    <CellIcon name={name as IconPack} width={24} height={24} />
  ) : (
    <OldIcon name={name} size={24} />
  )
}

const StyledContainer = styled.header`
  z-index: 100;
  background-color: ${COLOR.white};
`

const StyledHeader = styled.div`
  height: ${theme.variables.globalHeaderHeight}px;
  padding: 8px 16px;
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  z-index: 100;
`

const StyledLeftIconWrapper = styled.div`
  margin-right: 8px;
`

const StyledLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  left: 90px;
  top: 0px;
  height: 100%;
  img {
    height: 17px;
    cursor: pointer;
    user-select: none;
  }
`

const StyledLeftButtonGroup = styled.div`
  display: flex;
  align-items: center;
`

const StyledRightButtonGroup = styled.div<{ $hasSearchIcon: boolean }>`
  min-width: 30px;
  ${({ $hasSearchIcon }) =>
    $hasSearchIcon &&
    css`
      display: flex;
      align-items: center;
      ${StyledButton}:first-child {
        margin-right: 12px;
      }
    `}
`

const StyledLeftIconButton = styled.button`
  display: flex;
  align-items: center;
  width: 24px;
  height: 30px;
  padding: unset;
  background: none;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
`

const StyledButton = styled.button`
  width: 30px;
  height: 30px;
  padding: unset;
  line-height: 0;
  border: none;
  background: none;
  box-sizing: border-box;
  cursor: pointer;

  * {
    fill: #4c4c4c;
    color: #4c4c4c;
  }
`

const StyledSignInButton = styled.button`
  height: 32px;
  padding: 6px 14px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.typography.description2};
  color: ${({ theme }) => theme.colors.black};
  border-radius: 16px;
  cursor: pointer;
  &:lang(ja) {
    font-size: 12px;
    padding: 6px 10px;
  }
`

const StyledUserImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 24px;
`

export default GlobalHeader
